/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionIssuersRefreshMetadata } from "../funcs/organizationRemoteSessionIssuersRefreshMetadata.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionIssuerRefresh } from "../models/components/remotesessionissuerrefresh.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RefreshOrganizationRemoteSessionIssuerMetadataRequest,
  RefreshOrganizationRemoteSessionIssuerMetadataSecurity,
} from "../models/operations/refreshorganizationremotesessionissuermetadata.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RefreshOrganizationRemoteSessionIssuerMetadataMutationVariables = {
  request: RefreshOrganizationRemoteSessionIssuerMetadataRequest;
  security?: RefreshOrganizationRemoteSessionIssuerMetadataSecurity | undefined;
  options?: RequestOptions;
};

export type RefreshOrganizationRemoteSessionIssuerMetadataMutationData =
  RemoteSessionIssuerRefresh;

export type RefreshOrganizationRemoteSessionIssuerMetadataMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * refreshIssuerMetadata organizationRemoteSessionIssuers
 *
 * @remarks
 * Re-fetch an existing remote_session_issuer's RFC 8414 metadata document and persist the discovered values. Keyed by issuer id; serves both organizational and project-specific issuers in the caller's organization. Only RFC 8414-derived columns are written — endpoints, the *_supported arrays, client_id_metadata_document_supported, and the documentation URLs. Speakeasy behavior and display fields (oidc, passthrough, name, slug, logo, client setup documentation) are left alone. Requires org:admin.
 */
export function useRefreshOrganizationRemoteSessionIssuerMetadataMutation(
  options?: MutationHookOptions<
    RefreshOrganizationRemoteSessionIssuerMetadataMutationData,
    RefreshOrganizationRemoteSessionIssuerMetadataMutationError,
    RefreshOrganizationRemoteSessionIssuerMetadataMutationVariables
  >,
): UseMutationResult<
  RefreshOrganizationRemoteSessionIssuerMetadataMutationData,
  RefreshOrganizationRemoteSessionIssuerMetadataMutationError,
  RefreshOrganizationRemoteSessionIssuerMetadataMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRefreshOrganizationRemoteSessionIssuerMetadataMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeyRefreshOrganizationRemoteSessionIssuerMetadata(): MutationKey {
  return [
    "@gram/client",
    "organizationRemoteSessionIssuers",
    "refreshMetadata",
  ];
}

export function buildRefreshOrganizationRemoteSessionIssuerMetadataMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RefreshOrganizationRemoteSessionIssuerMetadataMutationVariables,
  ) => Promise<RefreshOrganizationRemoteSessionIssuerMetadataMutationData>;
} {
  return {
    mutationKey: mutationKeyRefreshOrganizationRemoteSessionIssuerMetadata(),
    mutationFn:
      function refreshOrganizationRemoteSessionIssuerMetadataMutationFn({
        request,
        security,
        options,
      }): Promise<RefreshOrganizationRemoteSessionIssuerMetadataMutationData> {
        const mergedOptions = {
          ...hookOptions,
          ...options,
          fetchOptions: {
            ...hookOptions?.fetchOptions,
            ...options?.fetchOptions,
            signal: combineSignals(
              hookOptions?.fetchOptions?.signal,
              options?.fetchOptions?.signal,
            ),
          },
        };
        return unwrapAsync(organizationRemoteSessionIssuersRefreshMetadata(
          client$,
          request,
          security,
          mergedOptions,
        ));
      },
  };
}
